import { useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { usePortal } from "@/pages/admin/PortalLayout";

const CATS = ["Ministries & Government Systems", "Payment Gateways", "Identity Systems", "SMS / Email / WhatsApp", "Maps & Location", "Other APIs", "LLM", "Speech"];
const field = "h-11 w-full rounded-xl border border-input bg-card px-3 text-sm";

export default function PortalIntegrations() {
  const [items, reload] = usePortal("/portal/integrations");
  const [f, setF] = useState({ name: "", purpose: "", category: "Other APIs", mode: "Simulated connector", endpoint: "" });
  const toggle = async (i) => { try { await api.patch(`/portal/integrations/${i.id}`, { enabled: !i.enabled }); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/portal/integrations", f); toast.success("Integration added"); setF({ ...f, name: "", purpose: "", endpoint: "" }); reload(); } catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <div className="space-y-6" data-testid="portal-integrations">
      <div><h1 className="text-2xl font-bold md:text-3xl">Integration registry</h1><p className="text-sm text-muted-foreground">Enable, disable or register new connectors and functions. All connectors run in <b>Simulated</b> mode.</p></div>
      <div className="grid gap-3 md:grid-cols-2">
        {(items || []).map((i) => (
          <div key={i.id} className="flex items-start gap-3 rounded-2xl border bg-card p-4 shadow-sm" data-testid={`integration-card-${i.id}`}>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold">{i.name} <span className="font-mono text-[11px] font-normal text-muted-foreground">{i.id}</span></p>
              <p className="text-xs text-muted-foreground">{i.purpose}</p>
              <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] font-semibold">
                <span className="rounded-full bg-[#FAF4E1] px-2 py-0.5 text-[#664D03]">Mode: {i.mode}</span>
                <span className={`rounded-full px-2 py-0.5 ${i.enabled ? "bg-[#D1E7DD] text-[#0F5132]" : "bg-[#E2E3E5] text-[#41464B]"}`}>{i.enabled ? "✓ Enabled" : "– Disabled"}</span>
              </div>
            </div>
            <Switch checked={!!i.enabled} onCheckedChange={() => toggle(i)} aria-label={`Toggle ${i.name}`} data-testid={`integration-toggle-${i.id}`} />
          </div>
        ))}
      </div>
      <form onSubmit={add} className="grid gap-3 rounded-2xl border bg-card p-5 shadow-sm md:grid-cols-2">
        <h2 className="text-base font-bold md:col-span-2 md:text-lg">Add a function / integration</h2>
        <input data-testid="integration-form-name" required placeholder="Name (e.g. Kahramaa smart meter)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={field} />
        <select data-testid="integration-form-category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={field}>{CATS.map((c) => <option key={c}>{c}</option>)}</select>
        <input data-testid="integration-form-purpose" required placeholder="Purpose" value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} className={`${field} md:col-span-2`} />
        <input data-testid="integration-form-endpoint" placeholder="Endpoint (optional, not called in sandbox)" value={f.endpoint} onChange={(e) => setF({ ...f, endpoint: e.target.value })} className={field} />
        <select data-testid="integration-form-mode" value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })} className={field}>
          <option>Simulated connector</option><option>Placeholder</option>
        </select>
        <button data-testid="integration-form-submit" className="inline-flex h-11 items-center justify-center gap-1.5 rounded-xl bg-primary font-semibold text-primary-foreground md:col-span-2"><Plus size={16} /> Register integration</button>
      </form>
    </div>
  );
}
